<script setup lang="ts">
import { UiCard, UiChip, UiIcon, UiText } from "@shopexperts/shared";
import type { BanRule } from "@shopexperts/legacy-api";
import BanListActionButton from "./BanListActionButton.vue";
import {
    formatBanDate,
    formatHits,
    formatMatchMode,
    formatRuleType,
} from "@/app/composables/ban-list/banListFormatters";

const props = defineProps<{
    rule: BanRule;
    isBusy?: boolean;
}>();

const emit = defineEmits<{
    (e: "edit"): void;
    (e: "toggle-active"): void;
    (e: "delete"): void;
    (e: "show-emails"): void;
    (e: "show-ips"): void;
    (e: "test"): void;
}>();
</script>

<template>
    <UiCard padding="md" class="transition-shadow hover:shadow-md">
        <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div class="flex min-w-0 flex-1 flex-col gap-2">
                    <div class="flex flex-wrap items-center gap-2">
                        <UiText as="p" variant="h6" class="font-semibold text-se-dark">
                            {{ rule.name }}
                        </UiText>
                        <UiChip
                            :text="rule.is_active ? 'Active' : 'Inactive'"
                            :variant="rule.is_active ? 'success' : 'neutral'"
                            size="sm"
                        />
                        <UiChip :text="formatRuleType(rule.type)" variant="outline" size="sm" />
                        <UiChip
                            v-if="rule.type === 'project_text'"
                            :text="formatMatchMode(rule.match_mode)"
                            variant="plain"
                            size="sm"
                            class="text-se-gray-600"
                        />
                    </div>

                    <UiText
                        v-if="rule.description"
                        as="p"
                        variant="p-small"
                        class="text-se-gray-600"
                        :text="rule.description"
                    />

                    <div class="flex flex-wrap items-center gap-2">
                        <UiText
                            as="span"
                            variant="caption-normal"
                            class="text-se-gray-500"
                            :text="rule.terms.length === 1 ? 'Term' : 'All terms must appear together'"
                        />
                        <UiChip
                            v-for="term in rule.terms"
                            :key="term"
                            :text="term"
                            size="sm"
                            rounded="full"
                            variant="neutral"
                        />
                    </div>
                </div>

                <div class="flex flex-wrap items-center gap-x-3 gap-y-2 xl:shrink-0">
                    <BanListActionButton label="Test" icon="preview" tone="primary" :disabled="isBusy" @click="emit('test')" />
                    <BanListActionButton label="Edit" icon="pencil" :disabled="isBusy" @click="emit('edit')" />
                    <BanListActionButton
                        :label="rule.is_active ? 'Deactivate' : 'Activate'"
                        :icon="rule.is_active ? 'lock' : 'lock-open'"
                        tone="warn"
                        :disabled="isBusy"
                        @click="emit('toggle-active')"
                    />
                    <BanListActionButton label="Delete" icon="delete" tone="danger" :disabled="isBusy" @click="emit('delete')" />
                </div>
            </div>

            <div class="grid grid-cols-2 gap-3 border-t border-se-gray-100 pt-4 lg:grid-cols-4">
                <div class="rounded-md bg-se-gray-50 px-3 py-2">
                    <UiText as="p" variant="caption-normal" class="text-xs uppercase tracking-wider text-se-gray-500" text="Hits" />
                    <UiText as="p" variant="p-small" class="mt-1 font-semibold tabular-nums text-se-dark" :text="formatHits(rule.hits_count)" />
                </div>
                <div class="rounded-md bg-se-gray-50 px-3 py-2">
                    <UiText as="p" variant="caption-normal" class="text-xs uppercase tracking-wider text-se-gray-500" text="Last matched" />
                    <UiText as="p" variant="p-small" class="mt-1 font-semibold text-se-dark" :text="formatBanDate(rule.last_matched_at)" />
                </div>

                <button
                    type="button"
                    class="rounded-md bg-se-gray-50 px-3 py-2 text-left transition-colors hover:bg-se-gray-100"
                    @click="emit('show-emails')"
                >
                    <UiText as="p" variant="caption-normal" class="text-xs uppercase tracking-wider text-se-gray-500" text="Emails banned" />
                    <span class="mt-1 flex items-center gap-1">
                        <UiText as="span" variant="p-small" class="font-semibold tabular-nums text-se-dark" :text="String(rule.banned_emails_count)" />
                        <UiIcon name="arrow-right" class="h-3.5 w-3.5 text-se-gray-500" />
                    </span>
                </button>

                <button
                    type="button"
                    class="rounded-md bg-se-gray-50 px-3 py-2 text-left transition-colors hover:bg-se-gray-100"
                    @click="emit('show-ips')"
                >
                    <UiText as="p" variant="caption-normal" class="text-xs uppercase tracking-wider text-se-gray-500" text="IPs recorded" />
                    <span class="mt-1 flex items-center gap-1">
                        <UiText as="span" variant="p-small" class="font-semibold tabular-nums text-se-dark" :text="String(rule.banned_ips_count)" />
                        <UiIcon name="arrow-right" class="h-3.5 w-3.5 text-se-gray-500" />
                    </span>
                </button>
            </div>

            <UiText
                as="p"
                variant="caption-normal"
                class="text-se-gray-500"
                :text="`Rule #${props.rule.id} · created ${formatBanDate(rule.created_at, 'unknown')} · updated ${formatBanDate(rule.updated_at, 'unknown')}`"
            />
        </div>
    </UiCard>
</template>
